import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const BookingDraftContext = createContext(null);

export function BookingDraftProvider({ children }) {
  // drafts is an array of objects: { id, status: 'minimised' | 'open', data: { ... } }
  const [drafts, setDrafts] = useState([]);
  
  // Create a new draft and open it
  const createDraft = useCallback((initialData = {}) => {
    const id = `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setDrafts(prev => [
      ...prev.map(d => ({ ...d, status: 'minimised' })), // Minimise others
      { 
        id, 
        status: 'open', 
        createdAt: Date.now(),
        data: {
          siteKey: '',
          serviceId: '',
          audience: 'patient',
          patientMode: 'search', // 'search' | 'selected' | 'new'
          patientId: null,
          patient: { firstName: '', lastName: '', email: '', phone: '' },
          providerId: '',
          confirmedDate: '',
          confirmedTime: '',
          paymentStatus: 'unpaid',
          sendPaymentLink: false,
          sendMedicalQuestionnaire: false,
          adminNotes: '',
          ...initialData
        } 
      }
    ]);
    return id;
  }, []);

  const updateDraft = useCallback((id, updates) => {
    setDrafts(prev => prev.map(d => 
      d.id === id ? { ...d, data: { ...d.data, ...updates } } : d
    ));
  }, []);

  const closeDraft = useCallback((id) => {
    setDrafts(prev => prev.filter(d => d.id !== id));
  }, []);

  const minimiseDraft = useCallback((id) => {
    setDrafts(prev => prev.map(d => 
      d.id === id ? { ...d, status: 'minimised' } : d
    ));
  }, []);

  const restoreDraft = useCallback((id) => {
    setDrafts(prev => prev.map(d => 
      d.id === id ? { ...d, status: 'open' } : { ...d, status: 'minimised' } // Only one open at a time
    ));
  }, []);

  // Global Keyboard Shortcut (Ctrl+B / Cmd+B)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        createDraft();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [createDraft]);

  return (
    <BookingDraftContext.Provider value={{
      drafts,
      createDraft,
      updateDraft,
      closeDraft,
      minimiseDraft,
      restoreDraft
    }}>
      {children}
    </BookingDraftContext.Provider>
  );
}

export function useBookingDraft() {
  const ctx = useContext(BookingDraftContext);
  if (!ctx) throw new Error('useBookingDraft must be used within BookingDraftProvider');
  return ctx;
}
